/*
 * ErrorLogView  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IView, IViewInfo } from "../../../view/IView";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { ErrorLogViewLayoutConfig } from "./ErrorLogViewLayout";
import { IEditor } from "../../../IEditor";
import { DeploymentService } from "../../../deployment/DeploymentService";
import { createEventEmitter, emitEvent, onEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { IErrorGroup } from "../../../errors/ErrorsTypes";
import { CommandsService } from "../../../commands/CommandsService";
import { ResourceService } from "../../../resources/ResourceService";

/**
 * View parameters
 */
interface IErrorLogViewParams {}

/**
 * Error Log View
 */
export class ErrorLogView implements IView<IErrorLogViewParams> {
	/** Deployment service */
	private deploymentService: DeploymentService;

	/** Deployment service */
	private commandsService: CommandsService;

	/** Resource service */
	private resourceService: ResourceService;

	/** Layout state */
	private layout: LayoutState;

	/** Errors changed event  */
	public onErrorsChanged = createEventEmitter<void>();

	/**
	 * View constructor
	 *
	 * @param dc Dependency container
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public constructor(private dc: IEditor) {
		this.deploymentService = dc.get("deploymentService");
		this.commandsService = dc.get("commandsService");
		this.resourceService = dc.get("resourceService");

		this.layout = new LayoutState(ErrorLogViewLayoutConfig);
	}

	/**
	 * Initializes view
	 * @param params Params
	 */
	public async init(params: IErrorLogViewParams): Promise<void> {
		onEvent(this.deploymentService.onErrorsChanged, this.handleErrorsChange);
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		removeAllEventListeners(this.onErrorsChanged);

		this.layout.dispose();
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo = (): IViewInfo => {
		return {
			label: "Errors",
			icon: "mdi/alert",
			canClose: true,
			isModified: false,
			renderWhenNotActive: true
		};
	};

	private handleErrorsChange = () => {
		emitEvent(this.onErrorsChanged);
	};

	public getErrors(): IErrorGroup[] {
		return this.deploymentService.getErrorGroups();
	}

	public reloadErrorList = (): Promise<void> => {
		return this.deploymentService.loadErrors();
	};

	public openErrorDocument({ resourceId, documentUri }: IErrorGroup): void {
		const id = resourceId || this.resourceService.getResourceByPath(documentUri)?.id;

		if (!id) {
			return;
		}

		this.commandsService.execute("resource.open", id);
	}
}
